<template>
  <section id="about" class="section about" aria-labelledby="about-title">
    <div class="container">
      <header class="section-heading">
        <p class="section-heading__index mono">02 / About</p>
        <div class="section-heading__copy">
          <h2 id="about-title">One engineer, the whole system.</h2>
          <p>
            Broad enough to make the architecture coherent. Close enough to
            the code to make it real.
          </p>
        </div>
      </header>

      <div class="about__grid">
        <div class="about__statement">
          <p>
            I work from interface to infrastructure—and stay accountable for
            what happens between them.
          </p>
          <ul class="about__roles mono" aria-label="Professional scope">
            <li>Hands-on development</li>
            <li>Solution architecture</li>
            <li>Technical leadership</li>
          </ul>
        </div>

        <div class="about__copy">
          <p>
            I’m a full-stack developer and solution architect with an
            enterprise .NET background spanning e-commerce, payments, and CMS
            work for major Nordic brands. My roles have ranged from hands-on
            development to CTO, with responsibility for technical direction
            and delivery.
          </p>

          <p>
            I work across backend services, cloud infrastructure, web
            interfaces, UX, and design systems. That range keeps architecture
            grounded in code, operations, and the people using the product.
          </p>

          <p>
            <a href="#work">Ulna</a> extends the same approach into systems
            programming: engine, server, Lua content runtime, Python tooling,
            and companion web product designed together. Away from the
            keyboard, I make time for the gym, after-work gatherings, and
            learning new things.
          </p>

          <ul class="about__chips" aria-label="Highlights">
            <li class="badge">Solution architecture</li>
            <li class="badge">Full-stack delivery</li>
            <li class="badge">Technical leadership</li>
            <li class="badge">Systems development</li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default { name: "About" };
</script>

<style lang="scss" scoped>
.about__grid {
  display: grid;
  grid-template-columns: minmax(280px, 0.8fr) minmax(0, 1.2fr);
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  background: var(--surface);
  box-shadow: var(--shadow-soft);
}

.about__statement {
  position: relative;
  display: flex;
  min-height: 460px;
  padding: clamp(34px, 5vw, 58px);
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  border-right: 1px solid var(--border);
  background:
    radial-gradient(circle at 12% 16%, rgba(124, 226, 201, 0.16), transparent 44%),
    linear-gradient(150deg, rgba(20, 32, 39, 0.96), rgba(9, 14, 20, 0.96));
}

.about__statement > p {
  position: relative;
  z-index: 1;
  margin: 0;
  color: var(--text);
  font-size: clamp(27px, 3vw, 38px);
  font-weight: 580;
  letter-spacing: -0.045em;
  line-height: 1.18;
}

.about__roles {
  position: relative;
  z-index: 1;
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  color: var(--muted);
  font-size: 9px;
  letter-spacing: 0.08em;
  list-style: none;
  text-transform: uppercase;
}

.about__roles li {
  display: flex;
  align-items: center;
  gap: 9px;
}

.about__roles li::before {
  content: "";
  width: 18px;
  height: 1px;
  background: var(--accent-2);
}

.about__copy {
  display: flex;
  padding: clamp(34px, 6vw, 70px);
  flex-direction: column;
  justify-content: center;
}

.about__copy > p {
  max-width: 63ch;
  margin: 0;
  color: var(--muted);
  font-size: 15px;
  line-height: 1.78;
}

.about__copy > p + p {
  margin-top: var(--space-5);
}

.about__copy a {
  color: var(--text);
  font-weight: 650;
}

.about__chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin: var(--space-6) 0 0;
  padding: 0;
  list-style: none;
}

@media (max-width: 780px) {
  .about__grid {
    grid-template-columns: 1fr;
  }

  .about__statement {
    min-height: 340px;
    border-right: 0;
    border-bottom: 1px solid var(--border);
  }
}

@media (max-width: 520px) {
  .about__grid {
    border-radius: var(--radius-lg);
  }

  .about__statement {
    min-height: 300px;
    padding: 30px 24px;
  }

  .about__copy {
    padding: 34px 24px;
  }
}
</style>
